import { Alert, Button } from '@mui/material';
import { useEffect, useState } from 'react';

/**
 * Tells a long-open tab that the console has been updated.
 *
 * The console downloads its code once and keeps running it; a tab opened
 * before a deploy goes on showing the old screens until it is reloaded, which
 * reads as "the release did not happen" (it had, 2026-09-28). Every few
 * minutes this asks the server which build it serves and, when that differs
 * from the build running here, says so with a Reload button.
 */

const CHECK_EVERY_MS = 5 * 60 * 1000;

/** The entry script the page was built with, e.g. "/assets/index-AbC123.js". */
function scriptOf(html: string): string | null {
  return /<script[^>]+type="module"[^>]+src="([^"]+)"/.exec(html)?.[1] ?? null;
}

function runningScript(): string | null {
  const tag = document.querySelector<HTMLScriptElement>('script[type="module"][src]');
  return tag === null ? null : new URL(tag.src, window.location.origin).pathname;
}

export function NewVersionBanner(): JSX.Element | null {
  const [outdated, setOutdated] = useState(false);

  useEffect(() => {
    const running = runningScript();
    // In development there is no built entry script to compare.
    if (running === null || !running.startsWith('/assets/')) return undefined;
    let stopped = false;
    const check = (): void => {
      fetch('/', { cache: 'no-store', credentials: 'same-origin' })
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const served = scriptOf(html);
          if (!stopped && served !== null && served !== running) setOutdated(true);
        })
        .catch(() => {
          // Offline or mid-deploy: try again next time.
        });
    };
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    const onFocus = (): void => check();
    window.addEventListener('focus', onFocus);
    check();
    return () => {
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (!outdated) return null;
  return (
    <Alert
      severity="info"
      role="status"
      sx={{ mb: 2 }}
      action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Reload</Button>}
    >
      A new version of the console is available. Reload to use it — anything you have typed but not saved on this page will be lost.
    </Alert>
  );
}
